import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Stack, Slider } from '@mui/material';

import { useAppAuth } from 'src/context/use-app-auth';
import { Role } from 'src/api/types/types';
import { AddFieldComponent } from 'src/components/add-field';
import { conditionalRender } from 'src/utils/ui/conditional-render';
import { useTranslation } from 'src/text';
import { useWindowDimensions } from 'src/utils/ui/dimensions';
import { t3 } from 'src/themes/custom';
import { reportLabels } from 'src/text/locales/default-en';
import { useReports } from '../reports-context';
import { GLOBAL_MARGIN_HORIZONTAL } from 'src/app/constants';

const SearchInfoComponent: React.FC = () => {
  const { isMobile } = useWindowDimensions();
  const { keywordLabels } = useTranslation();
  const { user } = useAppAuth();
  const isAddingKeywordsAllowed = user?.role === Role.admin;

  const { setTags: setReportTags, setRadiusData, radius } = useReports();
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (tags) {
      setReportTags(tags);
    }
  }, [tags]);

  const handleAddPressed = async (value: string) => {
    if (!user || !value) return;

    // Add to local tag list
    setTags(prev => [...prev, value]);
  };

  const handleRadiusChange = (event: Event, newValue: number | number[]) => {
    setRadiusData(newValue as number);
  };

  return (
    <Box mt={4}>
      <Typography mt={2} variant="h6">
        {reportLabels.selectRadius} ({radius} Km)
      </Typography>

      <Slider
        defaultValue={radius}
        sx={{
          width: isMobile ? '75%' : '25%',
          padding: GLOBAL_MARGIN_HORIZONTAL,
        }}
        value={radius}
        min={1}
        max={10}
        step={1}
        onChange={handleRadiusChange}
        valueLabelDisplay="auto"
        aria-label="Radius slider"
      />

      <Typography mt={2} variant="h3">
        {reportLabels.addKeywords}
      </Typography>

      {conditionalRender(
        isAddingKeywordsAllowed,
        <Box width={isMobile ? '100%' : '50%'}>
          <AddFieldComponent
            allowSpaces
            label={keywordLabels.typeKeyword}
            onAdd={handleAddPressed}
          />
        </Box>,
      )}

      <Box mt={3} width={isMobile ? '100%' : '50%'}>
        {tags.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {tags.map((tag, index) => (
              <Chip key={index} label={tag} sx={{ mb: 1 }} />
            ))}
          </Stack>
        ) : (
          <Typography sx={t3}>{keywordLabels.description}</Typography>
        )}
      </Box>
    </Box>
  );
};

export { SearchInfoComponent };
